import jwt_decode from 'jwt-decode';
import { push } from 'svelte-spa-router';
import { crefresh_token } from './http';
import type { TokenData, UserTokenData } from 'src/types/TokenData';

export const TOKEN_MAX_TIME = 0x7fffffff; // Maximum setTimeout is the max 32-bit signed integer

let tokenRefreshTimer: NodeJS.Timeout | null = null; // Centralized timer reference

/**
 * Extract user data from the JWT access token.
 * @param accessToken {string} accessToken - The JWT access token to decode.
 * @returns {extractUserTokenData} - User API URL, subscription status, and user ID.
 */
export const extractUserTokenData = (accessToken: string): UserTokenData => {
	const tokenData: TokenData = jwt_decode(accessToken);

	return {
		user_api_url: tokenData.user_api_url,
		is_sub: typeof tokenData.sa !== 'undefined' ? true : false,
		user_id: tokenData.user_id,
		temporary: tokenData.temporary === true,
	};
};

/**
 * Get the remaining time of JWT expiration.
 * If the JWT is invalid or has no expiration, returns 0.
 * Safe margin of 1500ms
 *
 * @param {string} jwt  - The JWT token to decode and check expiration.
 * @returns {number}  - time in milliseconds until the JWT expires.
 */
export const getRemainingTime = (jwt: string) => {
	try {
		// Decode the JWT to get the expiration time
		const decoded: any = jwt_decode(jwt);
		// Ensure `exp` exists and calculate time in milliseconds
		// Subtract 1500ms for safety margin
		const remaining = decoded.exp * 1000 - Date.now() - 1500;

		console.log('[TOKEN] Remaining time:', remaining);
		return remaining;
	} catch (error) {
		//Invalid JWT
		console.error('[TOKEN] Invalid JWT:', error);
		return 0; // Return 0 if JWT is invalid or has no expiration
	}
};

/**
 * Calculate the remaining time until the JWT expires.
 * If the token is already expired, returns 0.
 * @param {string} jwt - The JWT token to decode and check expiration.
 * @returns {number} - Remaining time in milliseconds until expiration or 0.
 */

export const expireTime = (jwt) => {
	const decoded: any = jwt_decode(jwt);
	// Ensure `exp` exists and calculate time in milliseconds
	const expirationTime = decoded.exp * 1000;
	// Calculate remaining time
	const remainingTime = expirationTime - Date.now() - 1500;

	//Safeguard remaning time to the maximum possible setTimeout time
	if (remainingTime > TOKEN_MAX_TIME) {
		console.warn('[TOKEN] Remaining time exceeds maximum timeout, setting to max timeout.');
		return TOKEN_MAX_TIME;
	}

	// Return non-negative timer
	return Math.max(remainingTime, 0);
};

/**
 * Refresh the token or redirect to token refresh fails.
 */
const refreshTokenOrRedirect = async () => {
	try {
		console.log('[TOKEN] Attempting to refresh token...');
		await crefresh_token();
		console.log('[TOKEN] Token refreshed successfully.');
	} catch (error) {
		console.error('[TOKEN] Failed, redirecting to login...');
		await push('/login');
	}
};

/**
 * Start watching expiration time when we receive jwt and clear existing ones.
 * @param {string} jwt
 */
export const startWatchExpire = (jwt) => {
	// Clear existing timer to prevent overlaps
	if (tokenRefreshTimer) {
		clearTimeout(tokenRefreshTimer);
		tokenRefreshTimer = null;
	}

	/**
	 * Decode jwt and get expiration time.
	 * If token expiration time is negative, set a timer for 5 minutes.
	 * Else set a timer for the remaining time until expiration.
	 */
	const timer = expireTime(jwt);
	if (timer <= 0) {
		tokenRefreshTimer = setTimeout(() => refreshTokenOrRedirect(), 300000); // delay - 5 minutes in ms
		return;
	}

	tokenRefreshTimer = setTimeout(() => refreshTokenOrRedirect(), timer);
};

/**
 * Clear the timeout for token expiration watcher.
 * Used when user logs out.
 */
export const clearTokenWatch = () => {
	if (tokenRefreshTimer) {
		clearTimeout(tokenRefreshTimer);
		tokenRefreshTimer = null;
	}
};
